/* eslint-disable indent */
import React, { useCallback, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Card,
  Stack,
  Typography,
  styled,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { FormattedMessage } from 'react-intl';
import { ConnectorInfo, keyValueApi } from 'types/types';
import { useUIContext } from 'modules/common/hooks/useUIContext';
import ErrorPage from 'modules/common/routes/ErrorPage/ErrorPage';
import { postRequest } from 'utils/helper';
import { DagsHistoryTable } from './DagsHistoryTable';
import { MultipleCustomSettings } from './DagCustomSettings/MultipleCustomSettings';
import { EditWorkflowDialog } from './EditWorkflowDialog';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 2, 1, 2)
  },
  dagDescription: {
    color: theme.palette.text.secondary,
    paddingBottom: '10px',
    fontWeight: theme.typography.fontWeightBold
  },
  head: {
    color: theme.palette.text.secondary,
    textOrientation: 'sideways',
    writingMode: 'vertical-lr'
  },
  stack: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  repliconLogoNew: {
    width: theme.spacing(6.2),
    paddingLeft: theme.spacing(1)
  },
  arrowIcon: {
    fontSize: 40,
    color: theme.palette.text.secondary
  },
  divButton: {
    paddingRight: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column'
  },
  card: { margin: theme.spacing(2, 0) },
  settingButton: {
    color: 'grey',
    marginLeft: '-3px',
    marginTop: theme.spacing(1)
  },
  editButton: { marginLeft: theme.spacing(2) },
  emptyDiv: { height: theme.spacing(2) }
}));

const DagSwitch = styled(Switch)(({ theme }) => ({
  width: 30,
  height: 16,
  padding: 0,
  margin: 8,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)'
    }
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#177ddc' : '#65C466'
      }
    }
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200
    })
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,.35)'
        : 'rgba(0,0,0,.25)',
    boxSizing: 'border-box'
  }
}));

type ConnectorSyncProps = {
  info: ConnectorInfo;
  dagId: string;
  dagDescription: string;
  arrowIcon: boolean;
  dagSwitchStatus: string;
  initialCustomSetting: any;
  mappedWorkflow?: string | null;
  dagSetting: { dagId: string; status: string } | null;
  setDagSetting: React.Dispatch<
    React.SetStateAction<{ dagId: string; status: string } | null>
  >;
};

export const ConnectorSyncPage: React.FC<ConnectorSyncProps> = ({
  info,
  dagDescription,
  dagId,
  arrowIcon,
  dagSwitchStatus,
  initialCustomSetting,
  mappedWorkflow,
  dagSetting,
  setDagSetting
}: ConnectorSyncProps) => {
  const dag = info.dagsSection.filter(
    (dagSection) => dagSection.dagId === dagId
  )[0];

  const [dagSwitch, setDagSwitch] = useState(dagSwitchStatus);
  const [dagHistorySaved, setDagHistorySaved] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dagSettings, setDagSettings] = useState(initialCustomSetting);
  const [expanded, setExpanded] = React.useState(
    Array.isArray(dag.customSettings) ? false : true
  );
  const [showErrorPage, setShowErrorPage] = useState<boolean>(false);
  const { genericKeyValues } = useUIContext() || {};
  const handleExpandClick = useCallback(() => {
    if (Array.isArray(dag.customSettings)) {
      setExpanded(!expanded);
    }
  }, [expanded, dag.customSettings]);
  const classes = useStyles();

  const handleEnableClick = useCallback(
    ({ status, value }: { status: string; value: string }) => {
      setDagSwitch(value);
      const body = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        dagId: dagId,
        status: status,
        key: keyValueApi.dagSettings
      };
      postRequest('/integration-settings-api/dag-configuration', body, false)
        .then((res) => {
          if (res.data) {
            setDagSetting(res.data);
          }
        })
        .catch((error: any) => {
          if (error.response) {
            setShowErrorPage(true);
            setDagSwitch(dagSwitch);
          }
        });
    },
    [info.shortName, info.companyKey, dagId, dagSwitch, setDagSetting]
  );

  const getExpandButtonText = useCallback((connectorName) => {
    const messageId =
      connectorName === 'replicon'
        ? 'repliconReportConnector.filters'
        : 'dagCustomSettings.settings';
    return <FormattedMessage id={messageId} />;
  }, []);

  const handleClick = useCallback(() => {
    if (dagSwitch === 'Enabled') {
      handleEnableClick({ status: 'No', value: 'Disabled' });
    } else {
      handleEnableClick({ status: 'Yes', value: 'Enabled' });
    }
  }, [dagSwitch, handleEnableClick]);

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <Card className={classes.card} elevation={1} data-testid="all-components">
      <div className={classes.container}>
        <Stack className={classes.stack} data-testid="stack-description">
          <Typography
            whiteSpace="nowrap"
            className={classes.dagDescription}
            data-testid={'header-description'}
          >
            {dagDescription}
          </Typography>
          {info.installed && info.shortName != 'replicon' && (
            <div className={classes.divButton} style={{ marginTop: '0px' }}>
              <Stack className={classes.stack}>
                <FormControlLabel
                  sx={{
                    color: dagSwitch === 'Disabled' ? 'grey' : 'black'
                  }}
                  control={
                    <DagSwitch
                      value={dagSetting ? dagSetting : ''}
                      checked={dagSwitch === 'Enabled' ? true : false}
                      onClick={handleClick}
                    />
                  }
                  label={dagSwitch}
                  labelPlacement="start"
                  data-testid="enable/disable button"
                />
                {genericKeyValues?.editWorkflowAccess && (
                  <Tooltip title="Map to a custom DAG">
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.editButton}
                      onClick={() => setEditDialogOpen(true)}
                      data-testid="edit-button"
                    >
                      <FormattedMessage id={'editWorkflow.edit'} />
                    </Button>
                  </Tooltip>
                )}
                <EditWorkflowDialog
                  open={editDialogOpen}
                  info={info}
                  dagId={dagId}
                  mappedWorkflow={mappedWorkflow}
                  setEditDialogOpen={setEditDialogOpen}
                />
              </Stack>
            </div>
          )}
        </Stack>
        <Stack className={classes.stack} data-testid="stack-context">
          <div>
            {info.shortName != 'replicon' && (
              <Stack className={classes.stack} data-testid="action-trigger">
                <Typography
                  className={classes.head}
                  data-testid={'check-trigger'}
                >
                  <FormattedMessage id={'dagHeading.trigger'} />
                </Typography>
                <img
                  data-testid="trigger-logo"
                  src={
                    arrowIcon
                      ? `connectorDescriptionPages/${info?.shortName}/${info?.shortName}.recipe.svg`
                      : 'circleRepliconLogoNew.svg'
                  }
                  className={classes.repliconLogoNew}
                />
                <ArrowRightIcon
                  className={classes.arrowIcon}
                  data-testid="RightIcon"
                />
                <Typography
                  className={classes.head}
                  data-testid={'check-actions'}
                >
                  <FormattedMessage id={'dagHeading.actions'} />
                </Typography>
                <img
                  data-testid="action-logo"
                  src={
                    arrowIcon
                      ? 'circleRepliconLogoNew.svg'
                      : `connectorDescriptionPages/${info?.shortName}/${info?.shortName}.recipe.svg`
                  }
                  className={classes.repliconLogoNew}
                />
              </Stack>
            )}
            <Stack className={classes.stack}>
              {Array.isArray(dag.customSettings) &&
                dag.customSettings.length != 0 &&
                info.installed && (
                  <Button
                    variant="text"
                    onClick={handleExpandClick}
                    className={classes.settingButton}
                    data-testid="setting-button"
                  >
                    {getExpandButtonText(info.shortName)}
                  </Button>
                )}
            </Stack>
          </div>
        </Stack>
        <Collapse
          in={expanded}
          timeout="auto"
          unmountOnExit
          data-testid="collapse-actions"
        >
          {dag.customSettings.length && (
            <MultipleCustomSettings
              info={info}
              dagId={dagId}
              dag={dag}
              setDagSettings={setDagSettings}
              initialCustomSetting={dagSettings}
              setDagHistorySaved={setDagHistorySaved}
            />
          )}
        </Collapse>
        {!info.installed ? (
          <div className={classes.emptyDiv}></div>
        ) : (
          <DagsHistoryTable
            companyKey={info.companyKey}
            identifier={`${info.shortName}_${dagId}`}
            dagHistorySaved={dagHistorySaved}
          />
        )}
      </div>
    </Card>
  );
};
